import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  buildInsightsData,
  PRODUCT_GROUPS_ALL,
  SEASONS_ALL,
  CHANNELS_ALL,
  STATUSES_ALL,
  type InsightsProduct,
  type InsightsReview,
} from '../lib/insightsMock';
import { AiSidebar } from '../components/AiSidebar';
import { Donut, HBars, Heatmap, Bullet, MultiSelectChip, useFilteredReviews } from '../components/Charts';
import { Stars } from '../components/Stars';
import { fmtNum, fmtPct, fmtRating } from '../lib/format';

type RangeKey = '7d' | '30d' | '90d' | 'ytd' | '12m' | 'all';

const RANGE_OPTIONS: ReadonlyArray<{ value: RangeKey; label: string }> = [
  { value: '7d', label: '7T' },
  { value: '30d', label: '30T' },
  { value: '90d', label: '90T' },
  { value: 'ytd', label: 'YTD' },
  { value: '12m', label: '12M' },
  { value: 'all', label: 'Gesamt' },
];

const STAR_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 5, label: '5★' },
  { value: 4, label: '4★' },
  { value: 3, label: '3★' },
  { value: 2, label: '2★' },
  { value: 1, label: '1★' },
];

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  published: 'Published',
  hidden: 'Hidden',
  rejected: 'Rejected',
};

// Build the dataset once at module load — it's deterministic.
const DATA = buildInsightsData();

export function InsightsPage() {
  const [params] = useSearchParams();
  const highlightId = params.get('rec');

  const [range, setRange] = useState<RangeKey>('30d');
  const [groups, setGroups] = useState<string[]>([]);
  const [seasons, setSeasons] = useState<string[]>([]);
  const [stars, setStars] = useState<number[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [loyalty] = useState<'all' | 'on' | 'off'>('all');

  const reviews = useFilteredReviews(DATA.reviews, {
    range,
    groups,
    seasons,
    stars,
    channels,
    statuses,
    loyalty,
  });

  const reset = () => {
    setRange('30d');
    setGroups([]);
    setSeasons([]);
    setStars([]);
    setChannels([]);
    setStatuses([]);
  };

  const activeFilterCount =
    (range !== '30d' ? 1 : 0) +
    groups.length +
    seasons.length +
    stars.length +
    channels.length +
    statuses.length;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h2 className="page-title">Insights</h2>
          <p className="page-sub">PO + QA Dashboard · {fmtNum(reviews.length)} Reviews im aktuellen Filter</p>
        </div>
        <div className="page-actions">
          {activeFilterCount > 0 && (
            <button type="button" className="btn" onClick={reset}>
              <span className="material-symbols-rounded">restart_alt</span>
              Filter zurücksetzen ({activeFilterCount})
            </button>
          )}
          <button type="button" className="btn" onClick={() => window.print()}>
            <span className="material-symbols-rounded">picture_as_pdf</span>
            PDF
          </button>
        </div>
      </div>

      <div
        className="card"
        style={{
          padding: 12,
          marginBottom: 16,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
          position: 'sticky',
          top: 60,
          zIndex: 4,
        }}
      >
        <span className="seg" role="group" aria-label="Zeitraum">
          {RANGE_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              className={range === o.value ? 'on' : ''}
              onClick={() => setRange(o.value)}
            >
              {o.label}
            </button>
          ))}
        </span>
        <MultiSelectChip
          label="Warengruppe"
          icon="category"
          options={PRODUCT_GROUPS_ALL.map((g) => ({ value: g, label: g }))}
          selected={groups as readonly (typeof PRODUCT_GROUPS_ALL)[number][]}
          onChange={(v) => setGroups(v as string[])}
        />
        <MultiSelectChip
          label="Saison"
          icon="calendar_month"
          options={SEASONS_ALL.map((s) => ({ value: s, label: s }))}
          selected={seasons as readonly (typeof SEASONS_ALL)[number][]}
          onChange={(v) => setSeasons(v as string[])}
        />
        <MultiSelectChip
          label="Sterne"
          icon="star"
          options={STAR_OPTIONS.map((s) => ({ value: String(s.value), label: s.label }))}
          selected={stars.map(String)}
          onChange={(v) => setStars(v.map(Number))}
        />
        <MultiSelectChip
          label="Kanal"
          icon="hub"
          options={CHANNELS_ALL.map((c) => ({ value: c, label: c }))}
          selected={channels as readonly (typeof CHANNELS_ALL)[number][]}
          onChange={(v) => setChannels(v as string[])}
        />
        <MultiSelectChip
          label="Status"
          icon="check_circle"
          options={STATUSES_ALL.map((s) => ({ value: s, label: STATUS_LABEL[s] ?? s }))}
          selected={statuses as readonly (typeof STATUSES_ALL)[number][]}
          onChange={(v) => setStatuses(v as string[])}
        />
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 16 }}>
          <ProductDrilldown products={DATA.products} />
          <div className="chart-grid">
            <HiddenPublishedCard reviews={reviews} />
            <HiddenReasonsCard reviews={reviews} />
            <WeekdayChannelHeatmap reviews={reviews} />
            <ModerationSLA reviews={reviews} />
            <TopMovers products={DATA.products} />
            <WorstPerformers products={DATA.products} />
            <CountryHeatmap reviews={reviews} />
          </div>
        </div>
        <AiSidebar highlightId={highlightId} />
      </div>
    </div>
  );
}

function ProductDrilldown({ products }: { products: readonly InsightsProduct[] }) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const matches = search.trim().length > 0
    ? products
        .filter(
          (p) =>
            p.name.toLowerCase().includes(search.toLowerCase()) ||
            p.sku.toLowerCase().includes(search.toLowerCase()),
        )
        .slice(0, 8)
    : [];

  const selected = selectedId ? products.find((p) => p.id === selectedId) ?? null : null;

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="card-title">Produkt-Drilldown</div>
          <div className="card-sub">Lifetime · 7 / 30 / 60 / 90-Tage-Ø mit Tendenz</div>
        </div>
      </div>
      <div style={{ position: 'relative', marginBottom: selected ? 16 : 0 }}>
        <span className="search" style={{ width: '100%' }}>
          <span className="material-symbols-rounded">search</span>
          <input
            type="search"
            placeholder="Produkt nach Name oder Artikel-Nr. suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </span>
        {matches.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              background: '#FFFFFF',
              border: '1px solid var(--border-1)',
              borderRadius: 12,
              boxShadow: '0 12px 32px rgba(0,0,0,0.10)',
              zIndex: 10,
              padding: 6,
            }}
          >
            {matches.map((p) => (
              <button
                key={p.id}
                type="button"
                style={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 10px',
                  border: 0,
                  background: 'transparent',
                  borderRadius: 8,
                  textAlign: 'left',
                  cursor: 'pointer',
                  font: '500 13px/18px var(--font-body)',
                }}
                onClick={() => {
                  setSelectedId(p.id);
                  setSearch('');
                }}
              >
                <span style={{ flex: 1, color: 'var(--fg-1)' }}>{p.name}</span>
                <span className="mono" style={{ color: 'var(--fg-3)' }}>{p.sku}</span>
                <Stars value={p.lifetimeAvg} />
              </button>
            ))}
          </div>
        )}
      </div>
      {selected && (
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
            <span style={{ font: '600 18px/24px var(--font-display)', color: 'var(--fg-1)' }}>{selected.name}</span>
            <span className="mono" style={{ color: 'var(--fg-3)' }}>{selected.sku}</span>
            <span className="chip" style={{ marginLeft: 'auto' }}>{selected.group}</span>
            <span className="chip">{selected.season}</span>
            <button
              type="button"
              className="btn"
              onClick={() => setSelectedId(null)}
              aria-label="Auswahl aufheben"
              style={{ padding: '4px 8px' }}
            >
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
            <DrillKpi label="Lifetime" value={selected.lifetimeAvg} count={selected.reviewCount} primary />
            <DrillKpi label="7 Tage" value={selected.last7Avg} delta={selected.last7Avg - selected.lifetimeAvg} count={selected.last7Count} />
            <DrillKpi label="30 Tage" value={selected.last30Avg} delta={selected.last30Avg - selected.lifetimeAvg} count={selected.last30Count} />
            <DrillKpi label="60 Tage" value={selected.last60Avg} delta={selected.last60Avg - selected.lifetimeAvg} />
            <DrillKpi label="90 Tage" value={selected.last90Avg} delta={selected.last90Avg - selected.lifetimeAvg} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div className="d-label">Top positive Themen</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {selected.topPositiveTopics.map((t) => (
                  <span
                    key={t}
                    className="chip"
                    style={{ background: 'rgba(71,185,124,0.10)', borderColor: 'rgba(71,185,124,0.30)', color: '#0D4F31' }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="d-label">Top negative Themen</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {selected.topNegativeTopics.map((t) => (
                  <span
                    key={t}
                    className="chip"
                    style={{ background: 'rgba(231,87,50,0.10)', borderColor: 'rgba(231,87,50,0.30)', color: '#701900' }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DrillKpi({
  label,
  value,
  delta,
  count,
  primary,
}: {
  label: string;
  value: number;
  delta?: number;
  count?: number;
  primary?: boolean;
}) {
  return (
    <div
      style={{
        background: primary ? 'rgba(42,135,177,0.06)' : '#FAFBFC',
        border: '1px solid var(--border-1)',
        borderRadius: 12,
        padding: 12,
      }}
    >
      <div className="d-label" style={{ marginBottom: 4 }}>{label}</div>
      <div style={{ font: '600 22px/28px var(--font-display)', color: 'var(--fg-1)' }}>
        {fmtRating(value)} ★
      </div>
      {count != null && (
        <div className="mono" style={{ font: '500 11px/15px var(--font-mono)', color: 'var(--fg-3)' }}>
          {fmtNum(count)} Reviews
        </div>
      )}
      {delta != null && (
        <div
          style={{
            font: '600 11px/16px var(--font-body)',
            color: delta >= 0 ? '#0D4F31' : '#701900',
            marginTop: 4,
          }}
        >
          {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(2).replace('.', ',')} vs. Lifetime
        </div>
      )}
    </div>
  );
}

function HiddenPublishedCard({ reviews }: { reviews: readonly InsightsReview[] }) {
  const counts = useMemo(() => {
    const c = { pending: 0, published: 0, hidden: 0, rejected: 0 };
    for (const r of reviews) c[r.status]++;
    return c;
  }, [reviews]);
  const total = reviews.length || 1;
  return (
    <div className="card span-6">
      <div className="card-head">
        <div>
          <div className="card-title">Hidden vs. Published</div>
          <div className="card-sub">Hidden-Quote {fmtPct(counts.hidden / total)} · Ziel &lt; 5 %</div>
        </div>
      </div>
      <Donut
        slices={[
          { label: 'Published', value: counts.published, color: 'var(--brand-green)' },
          { label: 'Pending', value: counts.pending, color: 'var(--brand-orange)' },
          { label: 'Hidden', value: counts.hidden, color: 'var(--fg-3)' },
          { label: 'Rejected', value: counts.rejected, color: 'var(--brand-red)' },
        ]}
        centerLabel={fmtPct(counts.published / total, 0)}
        centerSub="published"
      />
    </div>
  );
}

function HiddenReasonsCard({ reviews }: { reviews: readonly InsightsReview[] }) {
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of reviews) {
      if (r.hiddenReason) m.set(r.hiddenReason, (m.get(r.hiddenReason) ?? 0) + 1);
    }
    return m;
  }, [reviews]);
  const total = Array.from(counts.values()).reduce((s, x) => s + x, 0) || 1;
  return (
    <div className="card span-6">
      <div className="card-head">
        <div>
          <div className="card-title">Top Hidden-Gründe</div>
          <div className="card-sub">{fmtNum(total)} verborgene/abgelehnte Reviews</div>
        </div>
      </div>
      <HBars
        rows={Array.from(counts.entries()).map(([label, value]) => ({
          label,
          value,
          color: label.startsWith('Beleidigung') ? 'var(--brand-red)' : 'var(--brand-orange)',
        }))}
        format={(v) => `${fmtNum(v)} (${fmtPct(v / total, 0)})`}
      />
    </div>
  );
}

function WeekdayChannelHeatmap({ reviews }: { reviews: readonly InsightsReview[] }) {
  const cols = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const rows = ['E-Mail', 'PDP', 'MyAccount'];
  const values = useMemo(() => {
    const grid: number[][] = rows.map(() => Array(7).fill(0));
    const totals: number[] = rows.map(() => 0);
    for (const r of reviews) {
      const ri = rows.indexOf(r.channel);
      if (ri < 0) continue;
      const row = grid[ri];
      if (!row) continue;
      const cell = row[r.weekday] ?? 0;
      row[r.weekday] = cell + 1;
      totals[ri] = (totals[ri] ?? 0) + 1;
    }
    return grid.map((row, ri) => row.map((v) => (totals[ri] ? v / (totals[ri] ?? 1) : 0)));
  }, [reviews]);

  return (
    <div className="card span-12">
      <div className="card-head">
        <div>
          <div className="card-title">Reviews × Wochentag × Kanal</div>
          <div className="card-sub">Anteil der Reviews pro Wochentag, je Kanal</div>
        </div>
      </div>
      <Heatmap rows={rows} cols={cols} values={values} />
    </div>
  );
}

function ModerationSLA({ reviews }: { reviews: readonly InsightsReview[] }) {
  // Synthesise SLA buckets: distribute pending reviews into <4h / 4-24h / 24-48h / >48h
  const pending = reviews.filter((r) => r.status === 'pending').length;
  const buckets = [
    { label: '< 4 Std', value: Math.round(pending * 0.62), target: pending * 0.7 },
    { label: '4–24 Std', value: Math.round(pending * 0.24), target: pending * 0.2 },
    { label: '24–48 Std', value: Math.round(pending * 0.10), target: pending * 0.07 },
    { label: '> 48 Std', value: Math.round(pending * 0.04), target: pending * 0.03 },
  ];
  const overdue = (buckets[2]?.value ?? 0) + (buckets[3]?.value ?? 0);
  const max = pending || 1;
  return (
    <div className="card span-6">
      <div className="card-head">
        <div>
          <div className="card-title">Moderation-Backlog & SLA</div>
          <div className="card-sub">{fmtNum(pending)} Pending · {fmtNum(overdue)} überfällig</div>
        </div>
        {overdue > 0 && (
          <span
            className="badge flagged"
            style={{ alignSelf: 'flex-start' }}
            aria-label="überfällig"
          >
            {fmtNum(overdue)} überfällig
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {buckets.map((b) => (
          <Bullet
            key={b.label}
            label={b.label}
            value={b.value}
            target={b.target}
            max={max}
            goodAbove={false}
            format={(n) => fmtNum(Math.round(n))}
          />
        ))}
      </div>
    </div>
  );
}

function TopMovers({ products }: { products: readonly InsightsProduct[] }) {
  const sorted = [...products].map((p) => ({ ...p, delta: p.last30Avg - p.lifetimeAvg }));
  sorted.sort((a, b) => b.delta - a.delta);
  const top = sorted.slice(0, 5);
  const bottom = sorted.slice(-5).reverse();
  return (
    <div className="card span-6">
      <div className="card-head">
        <div>
          <div className="card-title">Top Movers (30 Tage)</div>
          <div className="card-sub">Stärkste Veränderungen ggü. Lifetime-Ø</div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <MoverCol title="Aufsteiger" products={top} positive />
        <MoverCol title="Absteiger" products={bottom} positive={false} />
      </div>
    </div>
  );
}

function MoverCol({
  title,
  products,
  positive,
}: {
  title: string;
  products: ReadonlyArray<InsightsProduct & { delta: number }>;
  positive: boolean;
}) {
  return (
    <div>
      <div className="d-label" style={{ marginBottom: 6 }}>
        {positive ? '▲' : '▼'} {title}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
        {products.map((p) => (
          <li
            key={p.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 8,
              padding: '6px 8px',
              borderRadius: 8,
              background: '#FAFBFC',
              border: '1px solid var(--border-1)',
              alignItems: 'center',
            }}
          >
            <span style={{ font: '500 13px/18px var(--font-body)', color: 'var(--fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.name}
            </span>
            <span
              className="mono"
              style={{
                color: positive ? '#0D4F31' : '#701900',
                font: '600 12px/16px var(--font-mono)',
              }}
            >
              {p.delta >= 0 ? '+' : ''}{p.delta.toFixed(2).replace('.', ',')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WorstPerformers({ products }: { products: readonly InsightsProduct[] }) {
  const worst = [...products].sort((a, b) => a.lifetimeAvg - b.lifetimeAvg).slice(0, 8);
  return (
    <div className="card span-6">
      <div className="card-head">
        <div>
          <div className="card-title">Worst Performer</div>
          <div className="card-sub">Lifetime-Ø sortiert aufsteigend · Ziel ≥ 4,0</div>
        </div>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Produkt</th>
            <th style={{ textAlign: 'right' }}>Reviews</th>
            <th style={{ textAlign: 'right' }}>Ø</th>
            <th style={{ textAlign: 'right' }}>30T</th>
          </tr>
        </thead>
        <tbody>
          {worst.map((p) => (
            <tr key={p.id}>
              <td>
                <div style={{ font: '500 13px/18px var(--font-body)' }}>{p.name}</div>
                <div className="mono" style={{ color: 'var(--fg-3)' }}>{p.sku}</div>
              </td>
              <td className="mono" style={{ textAlign: 'right' }}>{fmtNum(p.reviewCount)}</td>
              <td className="mono" style={{ textAlign: 'right', color: p.lifetimeAvg < 3.5 ? 'var(--brand-red)' : 'var(--fg-1)' }}>
                {fmtRating(p.lifetimeAvg)}
              </td>
              <td className="mono" style={{ textAlign: 'right', color: p.last30Avg < 3.5 ? 'var(--brand-red)' : 'var(--fg-1)' }}>
                {fmtRating(p.last30Avg)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CountryHeatmap({ reviews }: { reviews: readonly InsightsReview[] }) {
  // Aggregate by country: total + rating distribution.
  const byCountry = useMemo(() => {
    const m = new Map<string, { total: number; sum: number; dist: number[] }>();
    for (const r of reviews) {
      const cur = m.get(r.country) ?? { total: 0, sum: 0, dist: [0, 0, 0, 0, 0] };
      cur.total++;
      cur.sum += r.rating;
      const d = cur.dist[5 - r.rating];
      cur.dist[5 - r.rating] = (d ?? 0) + 1;
      m.set(r.country, cur);
    }
    return Array.from(m.entries())
      .map(([country, v]) => ({
        country,
        total: v.total,
        avg: v.sum / Math.max(1, v.total),
        dist: v.dist,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 12);
  }, [reviews]);

  return (
    <div className="card span-12">
      <div className="card-head">
        <div>
          <div className="card-title">Land-Performance</div>
          <div className="card-sub">Top {byCountry.length} Märkte · Sterne-Verteilung in % je Markt</div>
        </div>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Markt</th>
            <th style={{ textAlign: 'right' }}>Reviews</th>
            <th style={{ textAlign: 'right' }}>Ø</th>
            {[5, 4, 3, 2, 1].map((s) => (
              <th key={s} style={{ textAlign: 'center', width: 60 }}>{s}★</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {byCountry.map((row) => (
            <tr key={row.country}>
              <td>
                <span className="mono" style={{ background: '#F2F4F6', padding: '2px 8px', borderRadius: 6 }}>
                  {row.country}
                </span>
              </td>
              <td className="mono" style={{ textAlign: 'right' }}>{fmtNum(row.total)}</td>
              <td
                className="mono"
                style={{
                  textAlign: 'right',
                  color: row.avg < 3.5 ? 'var(--brand-red)' : row.avg >= 4.4 ? 'var(--brand-green)' : 'var(--fg-1)',
                }}
              >
                {fmtRating(row.avg)}
              </td>
              {row.dist.map((c, i) => {
                const pct = row.total > 0 ? c / row.total : 0;
                return (
                  <td
                    key={i}
                    style={{
                      textAlign: 'center',
                      background:
                        pct > 0.5 ? 'rgba(71,185,124,0.55)' :
                        pct > 0.3 ? 'rgba(60,180,165,0.40)' :
                        pct > 0.15 ? 'rgba(39,172,203,0.30)' :
                        pct > 0.05 ? 'rgba(238,136,45,0.25)' :
                        'rgba(231,87,50,0.18)',
                      color: pct > 0.5 ? '#FFFFFF' : 'var(--fg-1)',
                      font: '600 11px/16px var(--font-mono)',
                      borderRadius: 6,
                    }}
                  >
                    {(pct * 100).toFixed(0)}%
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
